import React, { FC, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { routes } from "@app/routing";
import { GisIntakeStep, STEP_ROUTE_SUFFIX } from "@app/service/intakeForm";
import { GIS_NO_ORDER_UID } from "@app/utils/gisEntrySource";
import { IntakeFormStepLayout, RadioOptionCard } from "../components";
import { useIntakeForm } from "../context";

const messages = defineMessages({
  title: {
    id: "intakeStepSelectTest.title",
    defaultMessage:
      "Please select which test you're requesting a genetic information session for.",
  },
  signateraLabel: {
    id: "intakeStepSelectTest.signateraLabel",
    defaultMessage:
      "Oncology Information Session (Signatera, Latitude, Altera)",
  },
  signateraDescription: {
    id: "intakeStepSelectTest.signateraDescription",
    defaultMessage: "Residual Disease Test (MRD)",
  },
  empowerLabel: {
    id: "intakeStepSelectTest.empowerLabel",
    defaultMessage: "Empower",
  },
  empowerDescription: {
    id: "intakeStepSelectTest.empowerDescription",
    defaultMessage: "Hereditary Cancer Test",
  },
  nextButton: {
    id: "intakeStepSelectTest.nextButton",
    defaultMessage: "Next",
  },
});

const TEST_OPTIONS = ["SIGNATERA", "EMPOWER"] as const;
type TestOption = (typeof TEST_OPTIONS)[number];

const IntakeStepSelectTest: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();
  const {
    prefilledData,
    updatePrefilledField,
    setCurrentStep,
    totalSteps,
    getNextStep,
    getPreviousStep,
  } = useIntakeForm();

  const currentStep = GisIntakeStep.SELECT_TEST;

  // The select-test step only exists in the no-order flow; an order-backed
  // intake pre-fills the test type, so guard against deep links into it.
  const isNoOrder = orderUidPublic === GIS_NO_ORDER_UID;

  const initialSelection = TEST_OPTIONS.find(
    (option) => option === (prefilledData?.testType ?? "").toUpperCase()
  );
  const [selectedOption, setSelectedOption] = useState<TestOption | null>(
    initialSelection ?? null
  );

  useEffect(() => {
    setCurrentStep(currentStep);
  }, [setCurrentStep]);

  const buildRoute = (step: GisIntakeStep | null) =>
    routes.scheduleAGeneticInformationSessionIntakePage(
      orderUidPublic || "",
      testUid || ""
    ) + (step ? STEP_ROUTE_SUFFIX[step] : "");

  const handleOptionChange = (value: TestOption) => {
    setSelectedOption(value);
    updatePrefilledField("testType", value);
  };

  const handleContinue = () => {
    if (selectedOption === null) return;
    // Empower is a Women's Health / hereditary-cancer test scheduled through
    // its own Acuity frame; it does not use the oncology intake questionnaire
    // (triage Rule 1 "Any Empower Test" → WH_EMPOWER). Routing decision is
    // already settled by this selection, so skip the remaining ONC intake
    // steps and go straight to the Empower scheduling frame, which books the
    // session matching the intent of the order the intake started from (none
    // here, so pre-test).
    if (selectedOption === "EMPOWER") {
      navigate(
        routes.scheduleAGeneticInformationSessionSchedulePage(
          orderUidPublic || "",
          testUid || ""
        )
      );
      return;
    }
    navigate(buildRoute(getNextStep(currentStep)));
  };

  const handleBack = () => {
    navigate(buildRoute(getPreviousStep(currentStep)));
  };

  if (!isNoOrder) {
    return <Navigate to={buildRoute(GisIntakeStep.CONFIRM_INFO)} replace />;
  }

  const options: {
    value: TestOption;
    label: string;
    description: string;
  }[] = [
    {
      value: "SIGNATERA",
      label: intl.formatMessage(messages.signateraLabel),
      description: intl.formatMessage(messages.signateraDescription),
    },
    {
      value: "EMPOWER",
      label: intl.formatMessage(messages.empowerLabel),
      description: intl.formatMessage(messages.empowerDescription),
    },
  ];

  return (
    <IntakeFormStepLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      title={intl.formatMessage(messages.title)}
      onContinue={handleContinue}
      onBack={handleBack}
      continueDisabled={selectedOption === null}
      continueLabel={intl.formatMessage(messages.nextButton)}
    >
      <div className="intake-form-step__options">
        {options.map((option) => (
          <RadioOptionCard
            key={option.value}
            id={`select-test-${option.value}`}
            name="select-test"
            label={option.label}
            description={option.description}
            selected={selectedOption === option.value}
            onChange={() => handleOptionChange(option.value)}
          />
        ))}
      </div>
    </IntakeFormStepLayout>
  );
};

export default IntakeStepSelectTest;
